<template>
	<div ref="currentElement" class="challenge-item" :class="{ 'clickable': isChallengeClickable(), 'has-overlay': challengeHasOverlay(), 'content-opened': isOpened, 'content-closed': !isOpened, 'contains-tag-new': isNew }">
		<!-- here add desktop class to prevent changing color on mobile -->
		<div v-if="challenge.showBlock=='default'" key="default" :class="{desktop: !isMobile}" class="block-default" @click.stop="onChallengeClick()">
			<div class="d-flex">
				<div class="challenge-image">
					<ResponsiveImage :height-proportion="100" :image="getChallengeImageUrl(challenge.image_id)" />
					
					<div v-if="!challenge.reward_type && getTranslation(challenge.points_multiple_text)" class="points points-text">
						{{ getTranslation(challenge.points_multiple_text) }}
					</div>
					
					<div v-if="!challenge.reward_type && !getTranslation(challenge.points_multiple_text) && challenge.points" class="points">
						+{{ challenge.points }}
					</div>

					<div v-if="challenge.reward_type == 'prize'" class="points">
						<font-awesome-icon icon="gift" />
					</div>

					<span v-if="isNew" class="challenge-tag-new">
						{{ t.tagNew }}
					</span>
				</div>

				<div class="challenge-main-block" :class="{'block-muted': showOverlay}">
					<div class="d-flex challenge-title-block">
						<div class="flex-grow-1 title">
							{{ getTranslation(challenge.name) }}
						</div>
						<div class="challenge-right-block">
							<span v-if="!isMobile && timer && timer.type == 'end' && timer.value" style="margin: 5px 0 0 5px;">
								{{ t.dashboardChallengeTimerPrefixEnd }}
								{{ timer.text }}
								<font-awesome-icon icon="flag-checkered" />
							</span>

							<!-- Challenge button menu -->
							<challenge-menu v-if="challenge.can_hide" :item="challenge" />
						</div>
					</div>

					<div class="challenge-intro-text">
						{{ getTranslation(challenge.description) }}
					</div>

					<template v-if="!isMobile && showButtonBar">
						<div class="button-row">
							<!-- Progress bar -->
							<progress-bar v-if="showProgressBar" :share="challenge.progressShare" :label="challenge.progressLabel" />

							<!-- Accept button -->
							<div v-if="showAcceptButton" class="pull-right">
								<button class="btn-challenge-accept" @click.stop="acceptChallenge()">
									{{ t.dashboardAcceptButton }}
								</button>
							</div>
						</div>
						<!-- Friends block -->
						<div v-if="showFriends" class="challenge-friends-placeholder" :class="{'mt-2': showAcceptButton}">
							<challenge-friends :challenge-id="challenge.id" />
						</div>
					</template>
					
					<!-- Challenge content block on desktop uses main-block width -->
					<div v-if="!isMobile && isOpened" :id="'challenge-content-' + challenge.id" class="challenge-content">
						<div v-if="loadingWidget" class="text-center">
							<Loader />
						</div>
						<transition-height name="fade">
							<div v-show="!loadingWidget">
								<component :is="widgetComponent" v-if="widgetComponent" ref="widgetElement" :challenge="challenge" :custom-properties="customProperties" env="user-interface" @vue:mounted="childMounted" />
							</div>
						</transition-height>
					</div>
				</div>
			</div>

			<div v-if="isMobile && showButtonBar" class="button-row">
				<!-- Progress bar -->
				<progress-bar v-if="showProgressBar" :share="challenge.progressShare" :label="challenge.progressLabel" />
				
				<!-- Timer -->
				<div v-if="showTimer" class="timer">
					{{ t.dashboardChallengeTimerPrefixEnd }}
					{{ timer.text }}
					<font-awesome-icon icon="flag-checkered" />
				</div>

				<!-- Accept button -->
				<div v-if="showAcceptButton">
					<button class="btn-challenge-accept" @click.stop="acceptChallenge()">
						{{ t.dashboardAcceptButton }}
					</button>
				</div>

				<!-- Friends block -->
				<div v-if="showFriends" class="challenge-friends-placeholder" :class="{'mt-3': showAcceptButton}">
					<challenge-friends :challenge-id="challenge.id" />
				</div>
			</div>

			<!-- Challenge content block on mobile must use all challenge width -->
			<div v-if="isMobile && isOpened" :id="'challenge-content-' + challenge.id" class="challenge-content">
				<div v-if="loadingWidget" class="text-center">
					<Loader />
				</div>
				
				<transition-height name="fade">
					<div v-show="!loadingWidget">
						<component :is="widgetComponent" v-if="widgetComponent" ref="widgetElement" :challenge="challenge" :custom-properties="customProperties" env="user-interface" @vue:mounted="childMounted" />
					</div>
				</transition-height>
			</div>

			<div v-if="showOverlay" class="overlay-content">
				<div v-if="timer && timer.value" class="timer">
					<FontAwesomeIcon icon="lock" class="mr-2" />

					<template v-if="timer.type == 'start'">
						{{ t.dashboardChallengeTimerPrefixStart }}
					</template>

					{{ timer.text }}
				</div>
			</div>
		</div>

		<transition-height name="fade" @after-leave="completedAfterLeave()">
			<div v-if="challenge.showBlock=='complete'" key="block-completed" class="block-completed" @click.stop.prevent="clickOnCompleted()">
				<div class="block-icon">
					<svg-challenge-success />
				</div>
				<div class="block-text">
					<div class="title">{{ t.dashboardCompleteChallengeTitle }}</div>
					<div class="challenge-intro-text">
						<div v-html="completeChallengeText" />
					</div>
				</div>
			</div>
		</transition-height>

		<div v-if="challenge.showBlock=='failed'" key="block-failed" class="block-failed" @click="challenge.showBlock='default'">
			<div class="block-icon">
				<svg-challenge-failed />
			</div>
			<div class="block-text" v-html="insertText(t.dashboardFailedChallengeText, timer.text)" />
		</div>

		<div v-if="challenge.showBlock=='error-aleady-completed'" key="block-already-completed" class="block-failed" @click.stop.prevent="removeChallengeFromList">
			<div class="block-icon">
				<svg-challenge-success />
			</div>
			<div class="block-text" v-html="t.dashboardFailedChallengeOtherErrorText" />
		</div>
	</div>
</template>

<script setup>
import { nextTick, ref, markRaw, computed, onMounted, onBeforeUnmount, toRaw, inject } from 'vue'
import { useRouter } from "vue-router"
import { includes } from "lodash-es"

import dayjs from "@/js/includes/dayjs"
import { getTranslation, getChallengeImageUrl, getTimerText, scrollTo } from "../../includes/helpers"
import { insertText } from '../../includes/helpers'
import ChallengeFriends from './challenge-friends.vue'
import ChallengeMenu from './challenge-menu.vue'
import ProgressBar from '../blocks/progress-bar.vue'

import SvgChallengeSuccess from "@/svg/customer/images/challenge-success.svg"
import SvgChallengeFailed from "@/svg/customer/images/challenge-failed.svg"

import { getPrize } from "@/js/classes/prize"
import { $emit, $on } from "@/js/includes/event-bus"
import { useStore } from '@/js/stores/index';
import { useChallenges } from '@/js/stores/challenges';
import ResponsiveImage from '../blocks/responsive-image.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import Loader from '../loader.vue'
import { loadRewards } from '@/js/includes/rewards'

const axios = inject('axios')
const store = useStore()
const challengeStore = useChallenges()
const { setPropertyForAll, setPropertyById, removeChallengeById } = useChallenges()
const router = useRouter()

// Refs
const currentElement = ref()
const widgetElement = ref()

// Props
const props = defineProps({
	challenge: {
		type: Object,
		required: true,
	},
})

// Emit
const emit = defineEmits(["challenge-opened"])

// Reactives
const challenge = ref(props.challenge)
const loadingWidget = ref(false)
const widgetComponent = ref(null)
const accepted = ref(null)
const timer = ref(false)
const timerRunning = ref(false)
const prize = ref(null)
const customProperties = ref({})

// Computeds
const t = computed(() => store.translations)
const showTimer = computed(() => timer.value?.type == 'end' && timer.value?.value)
const showFriends = computed(() => settings.value.userFriendsEnabled && !isOpened.value)
const showOverlay = computed(() => timer.value?.type == 'start')
const showButtonBar = computed(() => showProgressBar.value || showAcceptButton.value || showTimer.value || showFriends.value)
const showProgressBar = computed(() => accepted.value && challenge.value.progressLabel)
const isMobile = computed(() => store.isMobile)
const settings = computed(() => store.settings)
const isNew = computed(() => challenge.value.tags && includes(challenge.value.tags, 'new'))
const isOpened = computed(() => challenge.value.opened)
const isChallengeFullScreen = computed(() => isMobile.value && settings.value.challengeSettings.openFullScreen)
const completeChallengeText = computed(() => challenge.value.prize_id
	? insertText(t.value.dashboardCompleteChallengePrizeText, getTranslation(prize.value.name))
	: insertText(t.value.dashboardCompleteChallengeText, challenge.value.points)
)

const showAcceptButton = computed(() => {
	// if timer accepted or is "end" still show a button and let accept the challenge
	if(accepted.value || (timer.value?.value && timer.value?.type != 'end')) return false 

	return true
})

// Functions
// Register widget component for this challenge, but do it only once
async function registerWidget(type){
	const { default: component } = await import(`../../../challenge-types/${type}/widget.vue`);
	widgetComponent.value = markRaw(component);
}

// Check if we can click on challenge
function isChallengeClickable(){
	if(!accepted.value
		|| challenge.value.start_time && dayjs(challenge.value.start_time).toDate() > new Date()
		|| isOpened.value
		|| challenge.value.showBlock == 'complete'
		|| challenge.value.showBlock == 'failed'
	){
		return false
	}

	return true
}

function challengeHasOverlay(){
	if(!isMobile.value && (challenge.value.showBlock == 'complete' || !accepted.value)){
		return true
	}

	return false
}

// Click on challenge handler
function onChallengeClick(){
	if(!isChallengeClickable()){
		return false
	}

	if(isChallengeFullScreen.value){
		return router.push({name: 'challenge', params: {
			challengeId: challenge.value.id
		}})
	}

	// opened.value = true
	loadingWidget.value = true

	// Make shortcut for widget
	if(challenge.value.custom_data){
		customProperties.value = challenge.value.custom_data
	}

	// Set large image shortcut
	customProperties.value.imageId = challenge.value.image_large_id || false

	emit('challenge-opened', challenge.value)
	setPropertyForAll('opened', false)
	setPropertyById(challenge.value.id, 'opened', true)
}

async function acceptChallenge(){
	const challengeObject = toRaw(challenge.value)

	// Save user acceptance to the server 
	const response = await axios.post('/api/user-side/acceptUserChallenge', { challenge_id: challenge.value.id })
	
	// Update challenge data in the global store
	accepted.value = true
	challenge.value.accepted = true

	// Update specific challenge within a store
	// Find index of the challenge array element
	const index = toRaw(challengeStore.idsList)?.indexOf(challengeObject.id)
	const challenges = challengeStore.list

	challenges[index] = challengeObject

	challengeStore.setChallenges(challenges)

	// Set the timer
	setTimer(challenge.value, response.data, "end")

	onChallengeClick()
}

function clickOnCompleted(){
	challenge.value.showBlock = false
	removeChallengeById(challenge.value.id)
}

function completedAfterLeave(){
	$emit('achievement-collected', challenge.value)
}

function removeChallengeFromList()
{
	removeChallengeById(challenge.value.id)
}

function setTimer(time, type){
	// Check if we need a timer here
	if(!time){
		return
	}

	var targetTime = dayjs(time).toDate()

	if(targetTime > new Date()){
		var timerValue = Math.round((targetTime - new Date()) / 1000)

		timer.value = {
			text: getTimerText(timerValue), // 24 hours
			value: timerValue, // 24 hours
			target: targetTime, // 24 hours
			type: type, // "start", "end"
		}
	}
}

function launchTimer(){
	if(!timer.value) return;

	timerRunning.value = setInterval(() => {
		if(timer.value && timer.value.value > 0){
			timer.value.value--
			timer.value.text = getTimerText(timer.value.value)
		}else{
			// Timer ends. Remove it
			if(timerRunning.value){
				clearInterval(timerRunning.value)
				timerRunning.value = false
			}
			timer.value = false
		}
	}, 1000)
}

function childMounted(){
	// Stop execution if other challenge clicked. Detecting change here
	if(!isOpened.value) return;
	
	nextTick(() => {
		// Fixing if there any links in content to open in new window
		Array.from(currentElement.value.getElementsByTagName("a")).forEach(item => {
			item.target = "_blank"
		})

		// Scroll into view. It may be not well positioned
		scrollTo(currentElement.value)
	})

	if(!toRaw(widgetElement.value).waitForLoading){
		loadingWidget.value = false
	}
}

// Lifecycle
onBeforeUnmount(() => {
	if(timerRunning.value)	clearInterval(timerRunning.value)
})

onMounted(() => {
	// Register Challenger widget preview
	registerWidget(challenge.value.type)

	accepted.value = challenge.value.accepted

	// Check and set if we need a timer here
	setTimer(challenge.value.start_time, "start")

	// Check and set if we need a timer for end_time. Skip if timer is running for start time already
	if(challenge.value.end_time && !timer.value){
		setTimer(challenge.value.end_time, "end")
	}

	launchTimer()
})

// Events

// Callback when challenge interactively completed (correct answer in quiz, or similar)
$on('challenge-completed', async item => {
	// If google is connected we should remove email_confirmation challenge if it is visible. As it already completed 
	if(challenge.value.type == "email_confirmation" && item.type == "connect_google"){
		challenge.value.showBlock = 'complete'
	}

	if(challenge.value.id == item.id){
		// If the challenge is full screen - remove it from the list
		if(isChallengeFullScreen.value){
			$emit('achievement-collected', item) // Calling a handler to remove challenge from the list
			return; 
		}

		// Load the information about the prize won if needed
		if(challenge.value.prize_id){
			prize.value = await getPrize(challenge.value.prize_id)

			// Update the list of rewards
			loadRewards()
		}

		challenge.value.showBlock = 'complete'
		scrollTo(currentElement.value)
		return;
	}
})

// Callback when challenge is failed to complete (wrong answer in quiz, or similar)
$on('challenge-failed', (item, params = {}) => {
	if(challenge.value.id != item.id){
		return;
	}

	// Handle already completed challenge
	if(params.error_code == "ChallengeNotAvailable"){
		challenge.value.showBlock = 'error-aleady-completed'
		scrollTo(currentElement.value)
		return;
	}

	const delayInDays = params.delay ? params.delay : 1
	const tomorrow = new Date()
	tomorrow.setDate(tomorrow.getDate() + delayInDays)

	var delayInSeconds = 86400 * delayInDays
	timer.value = {
		text: getTimerText(delayInSeconds), // 24 hours
		value: delayInSeconds, // 24 hours
		target: tomorrow,
		type: 'start',
	}

	// Update start time of this challenge
	challenge.value.start_time = tomorrow

	launchTimer()

	// If this challenge was completed inline show failed screen. If it's full screen - skip this step
	if(!isChallengeFullScreen.value){
		setPropertyById(challenge.value.id, 'opened', false)
		challenge.value.showBlock = 'failed'
		
		scrollTo(currentElement.value)
	}
})

$on('challenge-widget-loaded', () => {
	loadingWidget.value = false
})
</script>
